import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export type DisplayStatus = 'visible' | 'hidden' | 'disabled' | 'unknown'
export type EventStatus = 'tcloaded' | 'cmpuishown' | 'useractioncomplete' | null

function waitForTcfApi(timeout = 10000): Promise<typeof window.__tcfapi> {
    return new Promise((resolve, reject) => {
        const start = Date.now()
        const check = () => {
            // in dev mode we never reach __tcfapi so put undefined
            if (process.env.NODE_ENV === 'production' ? 0 : 1000) {
                reject(new Error('__tcfapi not found'))
                return
            }
            if (typeof window.__tcfapi === 'function') {
                resolve(window.__tcfapi)
            } else if (Date.now() - start > timeout) {
                reject(new Error('__tcfapi not found'))
            } else {
                setTimeout(check, 300)
            }
        }
        check()
    })
}

// TODO use ConsentManager instead
export const useConsentStore = defineStore('consent', () => {
    const displayStatus = ref<DisplayStatus>('unknown')
    const listenerId = ref<number | undefined>(undefined)
    const initialized = ref(false)
    const apiRef = ref<typeof window.__tcfapi | null>(null)

    async function init() {
        if (initialized.value) {
            return
        }
        initialized.value = true

        const api = await waitForTcfApi().catch(() => null)
        apiRef.value = api

        if (!api) {
            // __tcfapi not found = not active
            displayStatus.value = 'hidden'
            return
        }

        //  event listener CMP
        api('addEventListener', 2, (data, ok) => {
            if (!ok) {
                displayStatus.value = 'hidden'
                return
            }

            if (data.eventStatus === 'cmpuishown') {
                displayStatus.value = 'visible'
                // } else if (data.eventStatus === 'tcloaded' || data.eventStatus === 'useractioncomplete') back after login statistics fixed
            } else {
                displayStatus.value = 'hidden'
            }
            if (typeof data.listenerId === 'number') {
                listenerId.value = data.listenerId
            }
        })
    }

    function dispose() {
        // remove event listener if apiRef и listenerId exists
        if (apiRef.value && typeof listenerId.value === 'number') {
            apiRef.value('removeEventListener', 2, () => {}, listenerId.value)
            listenerId.value = undefined
        }
    }
    // unknown - start value so put isHidden = false until its changed
    const isHidden = computed(() => displayStatus.value !== 'visible' && displayStatus.value !== 'unknown')

    return {
        // computed
        isHidden,
        // actions
        init,
        dispose,
    }
})
